import React, { FC, ReactNode, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, Dialog, Input } from '../../ui'

export interface LinkModalValues {
	text: string
	href: string
}

interface LinkModalProps {
	isOpen: boolean
	onClose: () => void
	title?: string
	icon?: ReactNode
	defaultText?: string
	defaultHref?: string
	onConfirm: (values: LinkModalValues) => void
	showRemove?: boolean
	onRemove?: () => void
}

const LinkModal: FC<LinkModalProps> = ({
	isOpen,
	onClose,
	title = 'Add link',
	icon,
	defaultText = '',
	defaultHref = '',
	onConfirm,
	showRemove = false,
	onRemove,
}) => {
	const {
		control,
		watch,
		reset,
		formState: { isDirty },
		handleSubmit,
	} = useForm<LinkModalValues>({
		defaultValues: { text: defaultText, href: defaultHref },
	})

	const { href } = watch()

	useEffect(() => {
		if (isOpen) {
			reset({ text: defaultText, href: defaultHref })
		}
	}, [isOpen, defaultText, defaultHref, reset])

	const handleClose = () => {
		onClose()
		reset({ text: '', href: '' })
	}

	if (!isOpen) return null

	const isValid = href?.trim().length > 0

	return (
		<Dialog
			isOpen={isOpen}
			onClose={handleClose}
				isDirty={isDirty}
				title={title}
				icon={icon}
				// overlayClassName=""
				controllers={
				<div className="flex items-center justify-end w-full gap-2">
					{showRemove && onRemove && (
						<Button
							variant="tertiary"
							label="Remove link"
							onClick={() => {
								onRemove()
								handleClose()
							}}
							className="mr-auto"
						/>
					)}
					<Button variant="tertiary" label="Cancel" onClick={handleClose} />
					<Button
						label="Save"
						onClick={handleSubmit((data) => {
							const linkHref =
								/^[\w+.-]+:/.test(data.href?.trim() ?? '') ||
								(data.href?.trim() ?? '').startsWith('/')
									? (data.href?.trim() ?? '')
									: `https://${data.href?.trim() ?? ''}`
							onConfirm({
								text: data.text?.trim() ?? '',
								href: linkHref,
							})
							handleClose()
						})}
						disabled={!isValid}
					/>
				</div>
			}
		>
			<div
				className="max-w-[440px] md:w-[440px] flex flex-col gap-4"
				data-tiptap-auxiliary
			>
				<Controller
					name="text"
					control={control}
					render={({ field }) => (
						<Input
							label="Label"
							labelClassName="font-normal text-text-main dark:text-text-main-dark"
							value={field.value}
							onChange={(e) => field.onChange(e.target.value)}
							inputClassName="px-5 h-12 rounded-[10px] dark:text-text-main-dark border-stroke-light dark:border-stroke-light-dark"
							placeholder="Link text"
						/>
					)}
				/>
				<Controller
					name="href"
					control={control}
					render={({ field }) => (
						<Input
							label="URL"
							labelClassName="font-normal text-text-main dark:text-text-main-dark"
							value={field.value}
							onChange={(e) => field.onChange(e.target.value)}
							inputClassName="px-5 h-12 rounded-[10px] dark:text-text-main-dark border-stroke-light dark:border-stroke-light-dark"
							placeholder="https://your-link.com"
						/>
					)}
				/>
			</div>
		</Dialog>
	)
}

export default LinkModal
